import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { ROICalculatorInputs, ROICalculatorResults } from '../types';

const ROICalculator = () => {
  const [inputs, setInputs] = useState<ROICalculatorInputs>({
    businessType: 'ecommerce',
    monthlyMessages: 1000,
    averageResponseTime: 240,
    employeeHourlyRate: 25
  });

  const [results, setResults] = useState<ROICalculatorResults | null>(null);

  const businessTypes = [
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'realestate', label: 'Real Estate' },
    { value: 'saas', label: 'SaaS' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'other', label: 'Other' }
  ];

  const calculateROI = () => {
    const manualTimeMinutes = inputs.monthlyMessages * (inputs.averageResponseTime / 60);
    const automatedTimeMinutes = inputs.monthlyMessages * 0.5;
    const timeSavedHours = (manualTimeMinutes - automatedTimeMinutes) / 60;
    const costSavings = timeSavedHours * inputs.employeeHourlyRate;
    const efficiencyGain = ((manualTimeMinutes - automatedTimeMinutes) / manualTimeMinutes) * 100;
    const monthlyInvestment = 499;
    const roi = ((costSavings - monthlyInvestment) / monthlyInvestment) * 100;

    setResults({
      timeSaved: Math.round(timeSavedHours),
      costSavings: Math.round(costSavings),
      efficiencyGain: Math.round(efficiencyGain),
      roi: Math.round(roi)
    });
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Calculate Your ROI
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              See exactly how much time and money you'll save with communication automation
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-xl font-semibold text-text-primary mb-6">
                Your Business Details
              </h3>

              <div className="space-y-6">
                <Select
                  name="businessType"
                  label="Business Type"
                  options={businessTypes}
                  value={inputs.businessType}
                  onChange={(value) => setInputs({ ...inputs, businessType: value as string })}
                />

                <Input
                  name="monthlyMessages"
                  autoComplete="off"
                  label="Monthly Messages"
                  type="number"
                  value={inputs.monthlyMessages}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputs({ ...inputs, monthlyMessages: parseInt(e.target.value) || 0 })}
                  description="Average number of customer messages per month"
                />

                <Input
                  name="averageResponseTime"
                  autoComplete="off"
                  label="Average Response Time (minutes)"
                  type="number"
                  value={inputs.averageResponseTime}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputs({ ...inputs, averageResponseTime: parseInt(e.target.value) || 0 })}
                  description="Current time to respond to each message"
                />

                <Input
                  name="employeeHourlyRate"
                  autoComplete="off"
                  label="Employee Hourly Rate ($)"
                  type="number"
                  value={inputs.employeeHourlyRate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputs({ ...inputs, employeeHourlyRate: parseInt(e.target.value) || 0 })}
                  description="Average hourly cost per support employee"
                />

                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={calculateROI}
                  iconName="Calculator"
                  iconPosition="left"
                >
                  Calculate Savings
                </Button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-xl font-semibold text-text-primary mb-6">
                Your Potential Savings
              </h3>

              {results ? (
                <div className="space-y-6">
                  <div className="bg-success/10 border border-success/20 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="Clock" size={24} className="text-success" />
                      <span className="text-sm text-text-secondary">Time Saved Monthly</span>
                    </div>
                    <div className="text-4xl font-bold text-success mb-1">
                      {results.timeSaved} hours
                    </div>
                    <p className="text-sm text-text-secondary">
                      That's {Math.round(results.timeSaved / 8)} full workdays reclaimed
                    </p>
                  </div>

                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="DollarSign" size={24} className="text-primary" />
                      <span className="text-sm text-text-secondary">Cost Savings Monthly</span>
                    </div>
                    <div className="text-4xl font-bold text-primary mb-1">
                      ${results.costSavings.toLocaleString()}
                    </div>
                    <p className="text-sm text-text-secondary">
                      ${(results.costSavings * 12).toLocaleString()} annually
                    </p>
                  </div>

                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="TrendingUp" size={24} className="text-accent" />
                      <span className="text-sm text-text-secondary">Efficiency Gain</span>
                    </div>
                    <div className="text-4xl font-bold text-accent mb-1">
                      {results.efficiencyGain}%
                    </div>
                    <p className="text-sm text-text-secondary">
                      Faster response times and better customer experience
                    </p>
                  </div>

                  <div className="bg-surface border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name="Target" size={24} className="text-text-primary" />
                      <span className="text-sm text-text-secondary">Return on Investment</span>
                    </div>
                    <div className="text-4xl font-bold text-text-primary mb-1">
                      {results.roi > 0 ? '+' : ''}{results.roi}%
                    </div>
                    <p className="text-sm text-text-secondary">
                      Monthly ROI after automation investment
                    </p>
                  </div>

                  <div className="pt-4 border-t border-border">
                    <Button
                      variant="default"
                      size="lg"
                      fullWidth
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Book Free Audit to Get Started
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <Icon name="Calculator" size={64} className="text-muted-foreground mb-4" />
                  <p className="text-text-secondary">
                    Enter your business details and click "Calculate Savings" to see your potential ROI
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;